import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type {
  EmailVerificationSendRequest,
  EmailVerificationSendResponse,
  EmailVerificationVerifyRequest,
  EmailVerificationVerifyResponse,
} from '@rheora/enrollment-contract';

import {
  postEmailVerificationSend,
  postEmailVerificationVerify,
} from './email-verification.fetchers';
import { funnelQueryKeys } from './funnel.queries';

/**
 * TanStack hooks for the E-SF05 email OTP. Pages call THESE, never the fetchers.
 *
 * ── BOTH ARE MUTATIONS, AND THAT IS A SECURITY DECISION FOR `verify` ───────
 * Both routes are POSTs with side effects (an email leaves the building; an attempt
 * budget is decremented), so a mutation is the honest modelling either way. But for
 * `verify` it is more than modelling: a `useQuery` would put its variables — the
 * OTP CODE — into the QUERY KEY, and TanStack persists query keys in its cache for
 * the lifetime of the tab. A one-time proof-of-control code would then sit in
 * memory, keyed and enumerable, long after it was spent. A mutation passes the
 * request as VARIABLES, which are never keyed or cached, so the code never enters
 * the query cache. This is the same reasoning that makes the credit check a
 * mutation for the SSN (see `credit.queries.ts`).
 *
 * ⚠️ Consequently there is deliberately NO `*QueryKeys` factory in this file. There
 * is nothing here to key: the send/verify pair has no cacheable read, and inventing
 * a key would be inventing the exact cache entry the mutation choice exists to
 * avoid. (Should a genuine READ ever be added — it should not — it would key as
 * `['shop', 'email-verification', …]`, the form this app already uses, and it would
 * still never carry the code.)
 *
 * ── ⚠️ CORRECTION: `verify` DOES NOW HAVE SOMETHING TO INVALIDATE ───────────
 * This header used to say there was nothing to invalidate, because "the
 * verified-address flag lives in the BFF's funnel session, is never fetched by the
 * SPA, and is read server-side by `/bff/credit/check`". The middle clause stopped
 * being true: `GET /bff/funnel` now returns the PROVEN ADDRESS so a browser refresh
 * does not force a fresh OTP send (which would burn one of only three per hour, on
 * both the session ledger and the victim-side recipient cap). So a successful VERIFY
 * makes the cached snapshot stale and invalidates it.
 *
 * `send` deliberately does NOT invalidate: issuing a code proves nothing, and the
 * snapshot only ever reports a REDEEMED address (`getVerifiedEmail` on the BFF
 * returns null until `verifiedAtMs` is set), so there is nothing new to read.
 */

/** Issue an OTP to the address the customer typed. CAPTCHA-gated server-side. */
export function useSendEmailOtpMutation(): UseMutationResult<
  EmailVerificationSendResponse,
  Error,
  EmailVerificationSendRequest
> {
  return useMutation({ mutationFn: postEmailVerificationSend });
}

/**
 * Redeem the emailed OTP. Resolves for every outcome — the caller branches on
 * `status` (`verified` / `incorrect` / `expired` / `locked`); only a genuine
 * failure rejects.
 */
export function useVerifyEmailOtpMutation(): UseMutationResult<
  EmailVerificationVerifyResponse,
  Error,
  EmailVerificationVerifyRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postEmailVerificationVerify,
    // ⚠️ Invalidates on ANY resolved outcome, not only `verified`. `incorrect`/
    // `locked` spend the challenge's attempt budget and `expired` retires it, so the
    // server's view of this session changed either way; branching on `status` here
    // would leave a stale snapshot behind on exactly the paths a customer retries.
    // The key carries no variables, so the one-time CODE cannot reach the cache.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}
